// Calcolo rendiconto lato client — specchio esatto di compute_report nel backend.
export const r2 = (x) => Math.round((Number(x || 0) + Number.EPSILON) * 100) / 100;

export const COST_CATEGORIES = [
  { key: "pulizie", label: "Pulizie", countLabel: "interventi" },
  { key: "lavanderia", label: "Lavanderia", countLabel: "fatture" },
  { key: "consumabili", label: "Consumabili", countLabel: null },
  { key: "amenities", label: "Amenities", countLabel: null },
  { key: "manutenzioni", label: "Manutenzioni", countLabel: null },
  { key: "altri", label: "Altri costi", countLabel: null },
];

const BASE_ALIASES = { ota_net: "netto_ota", netto_ota: "netto_ota", ota_gross: "lordo", lordo_ota: "lordo", total: "netto_dirette", netto_totale: "netto_dirette", netto_dirette: "netto_dirette", custom: "custom" };
export const normalizeBase = (b) => BASE_ALIASES[b] || "netto_dirette";

export const BASE_OPTIONS = [
  { value: "netto_dirette", label: "Netto OTA + Dirette" },
  { value: "netto_ota", label: "Netto OTA" },
  { value: "lordo", label: "Lordo" },
  { value: "custom", label: "Importo personalizzato" },
];

function platformJS(form, prefix) {
  const mode = form[`${prefix}_mode`] || "lordo";
  if (mode === "netto") {
    const net = r2(form[`${prefix}_net_input`]);
    return { mode, gross: net, net, deductions: [], hidden: 0, shown: 0, show_gross: false, show_net: true, only_net: true };
  }
  const gross = Number(form[`${prefix}_gross`]) || 0;
  const names = [["commissions", "Commissioni OTA"], ["withholding", "Ritenuta fiscale"], ["other", "Altre trattenute"]];
  let considered = 0, shown = 0, hidden = 0;
  const deductions = names.map(([key, label]) => {
    const amount = Number(form[`${prefix}_${key}`]) || 0;
    const considera = form[`${prefix}_${key}_considera`] !== false;
    const show = form[`${prefix}_${key}_show`] !== false;
    const effective = considera ? amount : 0;
    considered += effective;
    if (effective) { if (show) shown += effective; else hidden += effective; }
    return { key, label, amount, considera, show, effective };
  });
  return {
    mode, gross: r2(gross), net: r2(gross - considered), deductions,
    shown: r2(shown), hidden: r2(hidden),
    show_gross: form[`${prefix}_show_gross`] !== false,
    show_net: form[`${prefix}_show_net`] !== false,
    only_net: !!form[`${prefix}_only_net`],
  };
}

export function computeReportJS(form, autoCats) {
  const b = platformJS(form, "booking");
  const a = platformJS(form, "airbnb");
  const dGross = Number(form.direct_gross) || 0;
  const dAmt = Number(form.direct_deductions) || 0;
  const dConsidera = form.direct_deductions_considera !== false;
  const dShow = form.direct_deductions_show !== false;
  const direct_net = r2(dGross - (dConsidera ? dAmt : 0));
  const oi = Number(form.other_income) || 0;
  const oiC = form.other_income_considera !== false;

  const ota_net = r2(b.net + a.net);
  const ota_gross = r2(b.gross + a.gross);
  const total_net = r2(ota_net + direct_net + (oiC ? oi : 0));

  const baseKey = normalizeBase(form.management_base);
  const baseMap = {
    netto_ota: ota_net,
    netto_dirette: r2(ota_net + direct_net),
    lordo: r2(ota_gross + dGross),
    custom: Number(form.management_custom_amount) || 0,
  };
  const mgmtBase = baseMap[baseKey];
  const pct = Number(form.management_percent) || 0;
  const feeOverride = form.management_fee_override;
  const management_fee = feeOverride != null && feeOverride !== "" ? r2(feeOverride) : r2(mgmtBase * pct / 100);

  const overrides = form.cost_overrides || {};
  const cost_categories = {};
  let total_costs = 0;
  const shown_cost_rows = [];
  let hidden_costs_total = 0;
  for (const { key, label, countLabel } of COST_CATEGORIES) {
    const auto = (autoCats && autoCats[key]) || { auto_amount: 0, auto_count: 0, items: [] };
    const ov = overrides[key] || {};
    const considera = ov.considera !== false;
    const show_pdf = ov.show_pdf !== false;
    const amount = ov.amount != null ? r2(ov.amount) : r2(auto.auto_amount);
    const count = ov.count != null ? Number(ov.count) : (auto.auto_count || 0);
    cost_categories[key] = { key, label, auto_amount: r2(auto.auto_amount), auto_count: auto.auto_count || 0, amount, count, considera, show_pdf, overridden: ov.amount != null || ov.count != null };
    if (considera) {
      total_costs += amount;
      const showCount = key !== "pulizie" || form.show_cleaning_count !== false;
      const rowLabel = countLabel && count && showCount ? `${label} (${count} ${countLabel})` : label;
      if (show_pdf) shown_cost_rows.push({ label: rowLabel, amount });
      else hidden_costs_total += amount;
    }
  }
  const manual_costs = [];
  for (const cst of form.custom_costs || []) {
    if (cst.considera === false) continue;
    const amt = r2(cst.amount);
    manual_costs.push({ name: cst.name || "Costo", amount: amt, show_pdf: cst.show_pdf !== false });
    total_costs += amt;
    if (cst.show_pdf !== false) shown_cost_rows.push({ label: cst.name || "Costo", amount: amt });
    else hidden_costs_total += amt;
  }
  const resOverride = form.owner_result_override;
  const owner_result = resOverride != null && resOverride !== "" ? r2(resOverride) : r2(total_net - management_fee - total_costs);

  return {
    booking: b, airbnb: a,
    booking_net: b.net, airbnb_net: a.net, booking_gross: b.gross, airbnb_gross: a.gross,
    ota_net, ota_gross,
    direct_gross: r2(dGross), direct_net,
    direct_deductions: { amount: dAmt, considera: dConsidera, show: dShow },
    direct_show_gross: form.direct_show_gross !== false, direct_show_net: form.direct_show_net !== false,
    other_income: r2(oiC ? oi : 0), other_income_show: form.other_income_show !== false,
    total_net,
    management_base: baseKey, management_base_value: r2(mgmtBase), management_percent: pct, management_fee,
    cost_categories, manual_costs, shown_cost_rows, hidden_costs_total,
    total_costs, owner_result,
  };
}
